import type { Annotation } from '@jobos/shared';

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

// Map a value normalized 0..1 within `slot` to the equivalent 0..1 within `page`.
// Per-axis and exact regardless of any uniform/non-uniform scaling applied to both
// rects together (scale cancels), which is what lets us measure scaled thumbnails.
const mapX = (page: Rect, slot: Rect, sx: number) =>
  (slot.x + sx * slot.width - page.x) / page.width;
const mapY = (page: Rect, slot: Rect, sy: number) =>
  (slot.y + sy * slot.height - page.y) / page.height;

/**
 * Convert an annotation stored in slot-relative 0..1 space to page-relative 0..1
 * space. `renderedFontHeightPx` (text only) preserves the text's rendered size as
 * a viewBox-unit `size` = renderedFontHeightPx / page.height * 100.
 */
export function liftAnnotationToPage(
  page: Rect,
  slot: Rect,
  a: Annotation,
  renderedFontHeightPx?: number,
): Annotation {
  const wr = slot.width / page.width;
  const hr = slot.height / page.height;
  if (a.type === 'arrow' || a.type === 'line' || a.type === 'dimension') {
    return { ...a, x1: mapX(page, slot, a.x1), y1: mapY(page, slot, a.y1), x2: mapX(page, slot, a.x2), y2: mapY(page, slot, a.y2) };
  }
  if (a.type === 'circle') {
    return { ...a, x: mapX(page, slot, a.x), y: mapY(page, slot, a.y), rx: a.rx * wr, ry: a.ry * hr };
  }
  if (a.type === 'rect') {
    return { ...a, x: mapX(page, slot, a.x), y: mapY(page, slot, a.y), w: a.w * wr, h: a.h * hr };
  }
  const size = renderedFontHeightPx != null && page.height > 0
    ? (renderedFontHeightPx / page.height) * 100
    : a.size;
  return { ...a, x: mapX(page, slot, a.x), y: mapY(page, slot, a.y), ...(size != null ? { size } : {}) };
}
